/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import Gravatar from 'react-gravatar';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Check from '@material-ui/icons/Check';
import Edit from '@material-ui/icons/Edit';
import Shield from '@material-ui/icons/VerifiedUser';
import Erase from '@material-ui/icons/Delete';
import LockOpen from '@material-ui/icons/LockOpen';
import Lock from '@material-ui/icons/Lock';
import SearchIco from '@material-ui/icons/Search';
import FilterIco from '@material-ui/icons/FilterList';
import Refresh from '@material-ui/icons/Refresh';
import LinearProgress from '@material-ui/core/LinearProgress';
// material-ui/ core components
import withStyles from '@material-ui/core/styles/withStyles';
import Checkbox from '@material-ui/core/Checkbox';
import { Hidden, Zoom, Tooltip } from '@material-ui/core';
// material components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// style
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
// global variables
import { userRanks, userKinds } from 'config';
// our routes
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Paginator from 'components/common/Paginator';
import ModalConfirmation from 'components/common/ModalConfirmation';
import Filter from 'components/User/Filter';
import WithPermission from 'components/User/WithPermission';
import { LoadingAnimation } from 'components/common/Loading';
import { TableLoader } from 'components/common/TableLoader';
import AgencyIcon from 'assets/img/AgencyIco.svg';
import UserAgencyIcon from 'assets/img/UserAgencyIco.svg';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import { getUserPhotoUrl } from 'helpers/functions';

/**
 * @class
 * @extends React.Component
 */
class UserList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      modalIsOpen: false,
      userName: '',
      itemsToRemove: [],
      showFilters: false,
    };
  }

  /**
   * @description
   * Redirect to user/create route.
   */
  handleCreate = () => {
    const history = this.context;
    history.push(formatRoute(USER.CREATE, { userType: 'user' }));
  };

  /**
   * @description
   * Redirect to user/permission route.
   *
   * @param {number} id - Id of the user to show permissions.
   */
  handlePermissions = id => {
    const history = this.context;
    history.push(formatRoute(USER.PERMISSION, { id }));
  };

  /**
   * @description
   * Redirect to user/update/ route.
   *
   * @param {number} id - Id of the user to update.
   */
  handleUpdate = id => {
    const history = this.context;
    history.push(formatRoute(USER.UPDATE, { id }));
  };

  /**
   * @description
   * Redirect to user/read/ route.
   *
   * @param {number} id - Id of the user to read.
   */
  handleDetails = id => {
    const history = this.context;
    history.push(formatRoute(`${USER.DETAILS}#logs`, { id }));
  };

  /**
   * @description
   * Handle block, activate or delete action.
   *
   * @param {string} batchAction - Specific action.
   * @param {Array} itemToBlockOrDel - Items ids.
   */
  handleAction = (batchAction, itemToBlockOrDel) => {
    const { userDelete, userBlock, userUnBlock } = this.props;
    if (batchAction === 'block') {
      userBlock(itemToBlockOrDel);
    } else if (batchAction === 'activate') {
      userUnBlock(itemToBlockOrDel);
    } else if (batchAction === 'delete') {
      userDelete(itemToBlockOrDel);
    }
    this.setState({ selectedItems: [] });
  };

  /**
   * @description
   * Open destroying modal.
   *
   * @param {Array} itemsToRemove - Ids of the selected items.
   * @param {string} userName - User name of the selected item.
   */
  handleOpenModal = (itemsToRemove, userName) => {
    this.setState({ modalIsOpen: true, userName, itemsToRemove });
  };

  /**
   * @description
   * Callback to close destroying modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({ modalIsOpen: false, userName: '' });
  };

  /**
   * @description
   * Handle submit to confirm destroying selected items.
   */
  handleSubmit = () => {
    const { itemsToRemove } = this.state;
    const { userDelete } = this.props;

    userDelete(itemsToRemove);

    this.setState({
      itemsToRemove: [],
      selectedItems: [],
      modalIsOpen: false,
      userName: '',
    });
  };

  /**
   * @description
   * Map user rank to specific strings.
   *
   * @param {string} rank - User rank.
   * @returns {string} Mapped rank.
   */
  handleMapRanks = rank => {
    if (rank === userRanks.STAFF) return 'staff';
    if (rank === userRanks.USER) return 'user';
    return 'admin';
  };

  /**
   * @description
   * Return formatted type name.
   *
   * @param {string} type - Enum type.
   * @param {Function} t -  Format function.
   * @returns {string} Formatted name.
   */
  getUserType = (type, t) => {
    switch (type) {
      case userKinds.AGENCY:
        return t('user.list.user_role.agency');

      case userKinds.USER_AGENCY:
        return t('user.list.user_role.user_agency');

      default:
        return t('user.list.user_role.user');
    }
  };

  /**
   * @description
   * Map userType to specific strings.
   *
   * @param {string} userType - User type.
   * @param {string} action - Action.
   * @returns {string} Mapped user types.
   */
  handleMapPermissions = (userType, action) => {
    if (userType === userKinds.USER) return [`${action}_user`];
    if (userType === userKinds.USER_AGENCY)
      return [`${action}_useragency`, `${action}_ownership_useragency`];
    if (userType === userKinds.AGENCY) return [`${action}_agency`];
    return [];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All users from api.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return edges.map(({ node }) => ({
      id: node.id,
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      name: (
        <WithPermission
          alt={
            <React.Fragment>
              <span key="1" className={classes.avatarContainer}>
                {node.photo ? (
                  <img src={getUserPhotoUrl(node.photo)} alt="Avatar" />
                ) : (
                  <Gravatar email={node.email || node.fullname} />
                )}
                {node.userType === userKinds.AGENCY && (
                  <img
                    src={AgencyIcon}
                    alt="Agency identifier"
                    className="agencyIco"
                    data-cy="agencyIcon"
                    width="32px"
                    height="32px"
                  />
                )}
                {node.userType === userKinds.USER_AGENCY && (
                  <img
                    src={UserAgencyIcon}
                    alt="UserAgency identifier"
                    className="agencyIco"
                    data-cy="userAgencyIcon"
                  />
                )}
              </span>
              <span style={{ marginLeft: '8px' }} key="2">
                {`${node.fullname}`}
              </span>
            </React.Fragment>
          }
          allowPermissions={this.handleMapPermissions(node.userType, 'view')}
        >
          <div
            className={[
              'userDetail',
              classes.btnUserRead,
              node.isActive ? '' : classes.blockedUser,
            ].join(' ')}
          >
            <span key="1" className={classes.avatarContainer}>
              {node.photo ? (
                <img src={getUserPhotoUrl(node.photo)} alt="Avatar" />
              ) : (
                <Gravatar email={node.email || node.fullname} />
              )}
              {node.userType === userKinds.AGENCY && (
                <img
                  src={AgencyIcon}
                  alt="Agency identifier"
                  className="agencyIco"
                  data-cy="agencyIcon"
                />
              )}
              {node.userType === userKinds.USER_AGENCY && (
                <img
                  src={UserAgencyIcon}
                  alt="UserAgency identifier"
                  className="agencyIco"
                  data-cy="userAgencyIcon"
                />
              )}
            </span>
            <a
              data-cy="userDetailsBtn"
              className={['read', classes.anchorUserRead].join(' ')}
              id={`user-name-${node.id}`}
              href={formatRoute(`${USER.DETAILS}#logs`, { id: node.id })}
              onClick={e => {
                e.preventDefault();
                this.handleDetails(node.id);
              }}
            >
              {node.fullname}
            </a>
          </div>
        </WithPermission>
      ),
      userType: this.getUserType(node.userType, t),
      rank:
        node.rank !== userRanks.USER
          ? this.handleMapRanks(node.rank).toUpperCase()
          : '',
      email: node.email,
      phone: node.phone,
      actions: selectedItems.indexOf(node.id) === -1 && (
        // we've added some custom button actions
        <div className="actions-right">
          {/* use this button to edit the permissions row */}
          {node.rank === userRanks.STAFF && (
            <WithPermission allowPermissions={['change_permission']}>
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('user.list.tooltips.permissions')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="userPermissionsBtn"
                  justIcon
                  round
                  simple
                  onClick={() => {
                    this.handlePermissions(node.id);
                  }}
                  color="primary"
                  className="permissions"
                >
                  <Shield />
                </Button>
              </Tooltip>
            </WithPermission>
          )}
          {/* use this button to edit the data row */}
          <WithPermission
            allowPermissions={this.handleMapPermissions(
              node.userType,
              'change',
            )}
          >
            <Tooltip
              placement="top"
              TransitionComponent={Zoom}
              title={
                <React.Fragment>
                  {t('user.list.tooltips.edit')}
                  <span className={classes.arrow} />
                </React.Fragment>
              }
              classes={{
                tooltip: classes.bootstrapTooltip,
                tooltipPlacementTop: classes.bootstrapPlacementTop,
              }}
            >
              <Button
                data-cy="userUpdateBtn"
                justIcon
                round
                simple
                onClick={() => {
                  this.handleUpdate(node.id);
                }}
                color="primary"
                className="edit"
              >
                <Edit />
              </Button>
            </Tooltip>
          </WithPermission>
          <WithPermission
            allowPermissions={this.handleMapPermissions(
              node.userType,
              'change',
            )}
          >
            {node.isActive ? (
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('user.list.tooltips.block')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="userBlockUnblockBtn"
                  id="blockUser"
                  justIcon
                  round
                  simple
                  onClick={() => this.handleAction('block', [node.id])}
                  color="primary"
                  className={`block-user-${node.id}`}
                >
                  <Lock />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('user.list.tooltips.unblock')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="userBlockUnblockBtn"
                  id="unblockUser"
                  justIcon
                  round
                  simple
                  onClick={() => this.handleAction('activate', [node.id])}
                  color="success"
                  className={`block-user-${node.id}`}
                >
                  <LockOpen />
                </Button>
              </Tooltip>
            )}
          </WithPermission>
          {/* use this button to remove the data row */}
          <WithPermission
            allowPermissions={this.handleMapPermissions(
              node.userType,
              'delete',
            )}
          >
            <Tooltip
              placement="top"
              TransitionComponent={Zoom}
              title={
                <React.Fragment>
                  {t('user.list.tooltips.erase')}
                  <span className={classes.arrow} />
                </React.Fragment>
              }
              classes={{
                tooltip: classes.bootstrapTooltip,
                tooltipPlacementTop: classes.bootstrapPlacementTop,
              }}
            >
              <Button
                data-cy="userDeleteBtn"
                justIcon
                round
                simple
                onClick={() => {
                  this.handleOpenModal([node.id], node.fullname);
                }}
                color="danger"
                className={`delete-user-${node.id}`}
              >
                <Erase />
              </Button>
            </Tooltip>
          </WithPermission>
        </div>
      ),
    }));
  };

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  /**
   * @description
   * Handle search users.
   */
  handleSearch = () => {
    const { textToSearch, appliedFilter, filter } = this.props;
    filter({
      ...appliedFilter,
      contactInfo: textToSearch,
    });
  };

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = id => {
    const { selectedItems } = this.state;
    // if item is not selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({
        selectedItems: [...selectedItems, id],
      });
    }
  };

  /**
   * @description
   * To give a row color depending if it's blocked.
   *
   * @param {number} nodeId - Id of the node.
   * @returns {Object} - Style for the row.
   */
  renderRowColor = nodeId => {
    const { data } = this.props;
    const node = data.adminUsers.edges.find(
      ({ node: { id } }) => id === nodeId,
    );

    if (node.node.userType === userKinds.AGENCY) {
      return 'rgba(170, 105, 226, 0.2)';
    }

    if (node.node.userType === userKinds.USER_AGENCY) {
      return 'rgba(242, 175, 53, 0.2)';
    }

    if (node.node.isActive) {
      return '';
    }

    return 'rgba(0,0,0,0.1)';
  };

  /**
   * Description of ReactTable columns.
   *
   * @description Render for ReactTable Columns
   * @param {Array} edges - Data from api.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <b>{t('user.list.headers.name')}</b>
          </span>
        ),
        accessor: 'name',
        sortable: false,
        filterable: false,
        minWidth: 200,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <b>{t('user.list.headers.type')}</b>
          </span>
        ),
        accessor: 'userType',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <b>{t('user.list.headers.permissions')}</b>
          </span>
        ),
        accessor: 'rank',
        sortable: false,
        filterable: false,
        minWidth: 80,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <b>{t('user.list.headers.email')}</b>
          </span>
        ),
        accessor: 'email',
        sortable: false,
        filterable: false,
        minWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <b>{t('user.list.headers.phone')}</b>
          </span>
        ),
        accessor: 'phone',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header:
          edges.length !== selectedItems.length ? (
            <span className={classes.headerField}>
              <b>{t('user.list.headers.actions')}</b>
            </span>
          ) : (
            <span />
          ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 160,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All users from api.
   * @returns {undefined} No return.
   */
  handleSelectAll(edges) {
    const { selectedItems } = this.state;
    // if all are selected
    if (edges.length === selectedItems.length) {
      this.setState({
        selectedItems: [],
      });
    } else {
      this.setState({
        selectedItems: edges.map(item => item.node.id),
      });
    }
  }

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   *
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      classes,
      data,
      appliedFilter,
      loading,
      fullLoading,
      changeNumberOfRows,
      paginationDirection,
      changePage,
      numberOfRowsSelected,
      filter,
      itemProcessing,
      textToSearch,
      textChanged,
      refresh,
      t,
      failedIds,
      showNotification,
      closeNotification,
    } = this.props;
    const { selectedItems, showFilters, modalIsOpen, userName } = this.state;
    const edges = loading ? [] : data.adminUsers.edges;
    const columns = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={t(`common.failed_bulk_action`, {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />

        <ModalConfirmation
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseModal()}
          title={
            <span>
              {selectedItems.length > 0
                ? t('user.list.question')
                : t('user.details.modal.question')}
            </span>
          }
          description={userName}
          confirmLabel={`${t('user.details.modal.ok')}${
            selectedItems.length > 0 ? 's' : ''
          }`}
        />

        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>{t('user.title')}</h3>
                </GridItem>

                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                    style={{
                      marginRight: 24,
                    }}
                  >
                    <Refresh />
                    {t('common.refresh')}
                  </Button>

                  <WithPermission allowPermissions={['add_user', 'add_agency']}>
                    <Button
                      color="success"
                      default
                      onClick={this.handleCreate}
                      id="addUser"
                      data-cy="createUser"
                    >
                      {t('user.create.title')}
                    </Button>
                  </WithPermission>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>

                    <GridContainer
                      justify="space-between"
                      alignItems="center"
                      className={[
                        classes.loteActionsAnimation,
                        selectedItems.length > 0
                          ? classes.hideLote
                          : classes.showLote,
                      ].join(' ')}
                    >
                      <GridItem xs={12} sm={8} md={7}>
                        <div className={classes.searchFieldContainer}>
                          <CustomInput
                            formControlProps={{
                              className: `${classes.top} ${classes.search}`,
                            }}
                            inputProps={{
                              placeholder: t('user.list.placeholder'),
                              inputProps: {
                                'aria-label': 'Search',
                                className: classes.searchInput,
                                size: '30',
                                value: textToSearch,
                              },
                              endAdornment: (
                                <Button
                                  justIcon
                                  simple
                                  onClick={this.handleSearch}
                                  color="primary"
                                  className={classes.searchBtn}
                                >
                                  <SearchIco
                                    className={classes.inputAdornmentIcon}
                                  />
                                </Button>
                              ),
                              onChange: e => {
                                textChanged(e.target.value);
                              },
                              onKeyPress: e => {
                                if (e.key === 'Enter') {
                                  this.handleSearch();
                                }
                              },
                            }}
                          />
                          <Button
                            color="info"
                            round
                            simple={
                              !appliedFilter.userType.length &&
                              !appliedFilter.rank.length &&
                              appliedFilter.isActive === undefined &&
                              !showFilters
                            }
                            className={classes.filterBtn}
                            onClick={this.handleShowFilters}
                            id="filter"
                            key="filter"
                            data-cy="filterBtn"
                          >
                            <FilterIco />
                            <span>
                              {!appliedFilter.userType.length &&
                              !appliedFilter.rank.length &&
                              appliedFilter.isActive === undefined
                                ? t('user.list.filter')
                                : t('user.list.filtering')}
                            </span>
                          </Button>
                        </div>
                        <Hidden
                          only={['sm', 'md', 'lg', 'xl']}
                          implementation="css"
                        >
                          <GridContainer>
                            <GridItem xs={12}>
                              <div
                                className={`${classes.loteActionsAnimation}
                    ${showFilters ? classes.showFilters : classes.hideFilters}`}
                              >
                                <Filter
                                  filter={filter}
                                  appliedFilter={appliedFilter}
                                  showFilters={this.handleShowFilters}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                        </Hidden>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            data-cy="userListPaginator"
                            pageInfo={data.adminUsers.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <Hidden only={['xs']} implementation="css">
                      <GridContainer>
                        <GridItem xs={12}>
                          <div
                            className={`${classes.loteActionsAnimation}
                    ${showFilters ? classes.showFilters : classes.hideFilters}`}
                          >
                            <Filter
                              filter={filter}
                              appliedFilter={appliedFilter}
                              showFilters={this.handleShowFilters}
                            />
                          </div>
                        </GridItem>
                      </GridContainer>
                    </Hidden>
                    <GridContainer
                      alignItems="center"
                      className={[
                        classes.groupActions,
                        classes.loteActionsAnimation,
                        selectedItems.length > 0
                          ? classes.showLote
                          : classes.hideLote,
                      ].join(' ')}
                    >
                      <WithPermission
                        allowPermissions={[
                          'change_user',
                          'change_agency',
                          'change_useragency',
                        ]}
                      >
                        <Button
                          color="primary"
                          simple
                          onClick={() => {
                            this.handleAction('block', selectedItems);
                          }}
                          data-cy="blockLote"
                          id="blockSelectedUsers"
                        >
                          <Lock />
                          {t('user.list.tooltips.block')}
                        </Button>
                        <Button
                          color="primary"
                          simple
                          onClick={() => {
                            this.handleAction('activate', selectedItems);
                          }}
                          id="activeSelectedUsers"
                          data-cy="unblockLote"
                        >
                          <LockOpen />
                          {t('user.list.tooltips.unblock')}
                        </Button>
                      </WithPermission>
                      <WithPermission
                        allowPermissions={[
                          'delete_user',
                          'delete_agency',
                          'delete_useragency',
                        ]}
                      >
                        <Button
                          color="danger"
                          simple
                          onClick={() => {
                            this.handleOpenModal(selectedItems);
                          }}
                          id="eraseUsers"
                          data-cy="deleteLote"
                        >
                          <Erase />
                          {t('user.list.tooltips.erase')}
                        </Button>
                      </WithPermission>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={this.renderRows(edges)}
                              filterable
                              columns={columns}
                              loading={loading}
                              pageSize={
                                edges.length < numberOfRowsSelected
                                  ? edges.length
                                  : numberOfRowsSelected
                              }
                              expanded={loadingRows}
                              SubComponent={() => (
                                <div className={classes.loading}>
                                  <LoadingAnimation show />
                                </div>
                              )}
                              showPagination={false}
                              getTrProps={(state, rowInfo) => ({
                                style: {
                                  backgroundColor:
                                    rowInfo &&
                                    (selectedItems.indexOf(
                                      rowInfo.original.id,
                                    ) !== -1
                                      ? 'rgba(171, 221, 250, .8)'
                                      : this.renderRowColor(
                                          rowInfo.original.id,
                                        )),
                                  borderLeft:
                                    rowInfo &&
                                    failedIds.indexOf(rowInfo.original.id) !==
                                      -1
                                      ? '4px solid red'
                                      : '',
                                },
                              })}
                              className={`-striped -highlight ${classes.tbl}`}
                              noDataText={t('user.no_users')}
                              minRows={8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={4}>
                        {!fullLoading ? (
                          <Paginator
                            data-cy="userListPaginator"
                            pageInfo={data.adminUsers.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * UserList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop users query's results
 * @param textToSearch - text for search input
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop users query or mutations status
 * @param t - prop from HOC with string translations
 * @param failedIds - id of items that was failed after bulk action
 * @param showNotification - prop to know if notification will be showed or not
 * @callback filter - prop pass to Filter
 * @callback textChanged - callback for call when search input changes
 * @callback userDelete - callback to delete users
 * @callback userBlock - callback to block users
 * @callback userUnBlock - callback to activate users
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

UserList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  textToSearch: PropTypes.string,
  textChanged: PropTypes.func.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  userDelete: PropTypes.func.isRequired,
  userBlock: PropTypes.func.isRequired,
  userUnBlock: PropTypes.func.isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

UserList.defaultProps = {
  textToSearch: '',
};
// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const UserListWrapper = props => <UserList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(UserListWrapper),
);
